import { liteClient } from 'algoliasearch/lite';
import { SearchResponse } from '@algolia/client-search';
import { ExternalSearchCache } from './ExternalSearchCache';
import { SmartSendItemApiModel } from '~/lib/api/smartsend/types';
import getExternalSearchQueryParams, {
  appendTenantFilterToQueryParams,
  getExternalSearchQueryParamsAndAppendSource,
  getExternalSearchQueryParamsMarketplaceCount
} from './GetExternalSearchQueryParams';
import dayjs from 'dayjs';
import useSearchObjectsToSmartSendItems from '../../search/useSearchObjectsToSmartSendItems';
import { ExternalSearchDelivered } from '~/apps/smartsend/lib/consts/ListyConsts';
import { SearchObject } from '~/components/search/algolia/Models';
import { useMeApi } from '~/lib/api/me/use-me-api';
import { useCallback } from 'react';
import { useTenant } from '@parcel2go/npm-p2g-app';
import { warnIfStalled } from '~/lib/api/search-diagnostics';
import { getSearchKeyRefreshAt, getSearchKeyValidUntil } from '~/lib/api/me/search-key-expiry';

export interface SearchObjectResponse {
  items: SmartSendItemApiModel[];
  totalItems: number;
  totalPages: number;
}

const useExternalSearch = () => {
  const { tenant } = useTenant();
  const { searchObjectsToSmartSendItems } = useSearchObjectsToSmartSendItems();
  const { getSearchToken } = useMeApi();

  const getExternalSearchCredentials = useCallback(async () => {
    const cached = ExternalSearchCache.creds[tenant];
    const now = dayjs().unix();
    if (cached && cached.expires >= now) {
      return cached;
    }

    // Single-flight. The cache is only written once the request resolves, so concurrent
    // callers each used to start their own - a single My Orders load fired six `/search`
    // requests. That endpoint is the one the edge stalls on most (IBUG-2599), so share the
    // pending promise rather than multiplying the load.
    let pending = ExternalSearchCache.inFlight[tenant];
    if (!pending) {
      pending = (async () => {
        const cfg = await warnIfStalled(getSearchToken(), 'token');
        if (cfg && cfg.success && cfg.result) {
          // The key is valid for as long as the edge signed it for (an hour), not a client-side
          // guess; it is replaced a margin ahead of that.
          const validUntil = getSearchKeyValidUntil(cfg.result.token, dayjs().unix());
          const creds = { ...cfg.result, validUntil, expires: getSearchKeyRefreshAt(validUntil) };
          ExternalSearchCache.creds[tenant] = { ...creds };
          return creds;
        }
        return undefined;
      })();
      ExternalSearchCache.inFlight[tenant] = pending;

      // Always clear the slot, so one failed request does not wedge every later caller.
      // The `catch` keeps this bookkeeping chain from raising an unhandled rejection; the
      // rejection still reaches whoever awaits `pending`.
      void pending
        .catch(() => undefined)
        .finally(() => {
          if (ExternalSearchCache.inFlight[tenant] === pending) {
            delete ExternalSearchCache.inFlight[tenant];
          }
        });
    }

    // Two distinct failure modes. A response that resolves without usable creds falls back to
    // the cached key only while that key still works - an expired key only produces Algolia
    // "validUntil parameter expired" errors (B12). A rejected request still throws out of here
    // rather than falling back - callers have always seen those, and swallowing them would hide
    // edge failures from Sentry.
    const fresh = await pending;
    if (fresh) {
      return fresh;
    }
    return cached && cached.validUntil > dayjs().unix() ? cached : undefined;
  }, [getSearchToken, tenant]);

  const getExternalSearchQueryCount = useCallback(
    async (filtersQuery: string): Promise<number> => {
      const creds = await getExternalSearchCredentials();
      if (creds) {
        const searchClient = liteClient(creds.application, creds.token);
        const res = await searchClient.search<SearchObject>([
          {
            indexName: creds.indexName,
            query: '',
            params: {
              filters: appendTenantFilterToQueryParams(filtersQuery, tenant),
              hitsPerPage: 1,
              page: 1
            }
          }
        ] as any);
        const result = res?.results?.[0];
        return (result && 'nbHits' in result ? result.nbHits : 0) || 0;
      } else {
        return Promise.resolve(0);
      }
    },
    [getExternalSearchCredentials, tenant]
  );

  const getExternalSearch = useCallback(
    async (
      page: number,
      pageSize: number,
      filtersQuery: string,
      query: string
    ): Promise<SearchResponse<SearchObject>> => {
      const creds = await getExternalSearchCredentials();
      if (creds) {
        const searchClient = liteClient(creds.application, creds.token);
        const res = await searchClient.search<SearchObject>([
          {
            indexName: creds.indexName,
            query: query,
            params: {
              filters: appendTenantFilterToQueryParams(filtersQuery, tenant),
              hitsPerPage: pageSize,
              page: page
            }
          }
        ] as any);
        return res?.results?.[0] as SearchResponse<SearchObject>;
      } else {
        return Promise.resolve(null);
      }
    },
    [getExternalSearchCredentials, tenant]
  );

  const searchExternalSearchAndPollyFillItems = useCallback(
    async (page: number, pageSize: number, filters: string, query: string): Promise<SearchObjectResponse> =>
      getExternalSearch(page, pageSize, filters, query).then((r) =>
        searchObjectsToSmartSendItems(r.hits, r.nbHits, r.nbPages)
      ),
    [getExternalSearch, searchObjectsToSmartSendItems]
  );

  const getByOrderLineId = useCallback(
    async (id: number): Promise<SearchObject> => getExternalSearch(0, 1, '', String(id)).then((r) => r.hits[0]),
    [getExternalSearch]
  );

  const getExternalSearchQueryCountByListyKey = useCallback(
    async (listyKey: string): Promise<number> =>
      getExternalSearchQueryCount(getExternalSearchQueryParams('DEFAULT', listyKey)),
    [getExternalSearchQueryCount]
  );

  const getExternalSearchQueryCountByPaidSource = useCallback(
    async (sources: string[], filter: string = ExternalSearchDelivered): Promise<number> =>
      getExternalSearchQueryCount(getExternalSearchQueryParamsAndAppendSource('LAST6MONTHS', filter, sources)),
    [getExternalSearchQueryCount]
  );

  const getExternalSearchQueryMarketplaceOrderCount = useCallback(
    async (filter: string = ExternalSearchDelivered): Promise<number> =>
      getExternalSearchQueryCount(getExternalSearchQueryParamsMarketplaceCount('LAST6MONTHS', filter)),
    [getExternalSearchQueryCount]
  );

  return {
    searchExternalSearchAndPollyFillItems,
    getExternalSearchQueryCountByListyKey,
    getExternalSearchQueryCount,
    getExternalSearchQueryCountByPaidSource,
    getExternalSearchCredentials,
    getExternalSearchQueryMarketplaceOrderCount,
    getByOrderLineId
  } as const;
};

export default useExternalSearch;
